import { channel } from 'redux-saga';
import { call, cancel, put, select, take, takeEvery } from 'redux-saga/effects';
import { get } from 'lodash';
import { toast } from 'react-toastify';

import { getPagesHistory } from '../../containers/PagesHistory/selectors';
import * as moment from 'moment';

import * as actions from './actions';

import { getType } from 'typesafe-actions';
import { WritingInterval, Page, PageAnalysis } from '../../Models';
import { getUserProfile } from '../../containers/UserProfile/selectors';
import { IUserProfileState } from '../../containers/UserProfile/reducer';
import { createPage } from '../../containers/PagesHistory/actions';

const fileReaderChannel = channel();

export interface ExportPage {
  readonly text: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly startedWritingAt: string;
  readonly finishedWritingAt: string;
  readonly isDeleted: boolean;
  readonly lastSyncDate?: string;
  readonly wordsGoal: number;
  readonly analysis?: PageAnalysis;
  readonly writingIntervals: WritingInterval[];
}

function* exportAllPagesToFileSaga() {
  const pagesHashmap: { [key: string]: Page } = yield select(getPagesHistory);
  const pages = Object.values(pagesHashmap);

  const pagesToExport: ExportPage[] = pages.map(page => {
    return {
      text: page.text,
      createdAt: page.createdAt,
      updatedAt: page.updatedAt,
      startedWritingAt: page.startedWritingAt,
      finishedWritingAt: page.finishedWritingAt,
      isDeleted: page.isDeleted,
      lastSyncDate: page.lastSyncDate,
      wordsGoal: page.wordsGoal,
      analysis: page.analysis,
      writingIntervals: page.writingIntervals,
    };
  });

  const fileContent = JSON.stringify(pagesToExport, null, 2);

  const fileUrl = window.URL.createObjectURL(new Blob([fileContent], { type: 'text/json' }));
  const downloadLink: HTMLAnchorElement = document.createElement('a');

  downloadLink.download = `MorningPages-Backup-${moment().format(`YYYY-MM-DD`)}.json`;

  downloadLink.href = fileUrl;

  document.body.appendChild(downloadLink);

  downloadLink.click();

  document.body.removeChild(downloadLink);
}

const readFile = (file: any) => {
  const fileReader = new FileReader();

  fileReader.addEventListener('load', event => {
    fileReaderChannel.put(actions.importPagesFromFileFileReadSuccess(get(event, ['target', 'result'])));
  });

  fileReader.addEventListener('error', error => {
    fileReaderChannel.put(actions.importPagesFromFileFileReadError(error));
  });

  fileReader.readAsText(file);
};

function* importPagesFromFileSaga(action: ReturnType<typeof actions.importPagesFromFile>) {
  yield call(readFile, action.payload);

  const result = yield take(fileReaderChannel);

  if (result.type === getType(actions.importPagesFromFileFileReadError)) {
    yield put(actions.importPagesFromFileError(result.payload));
    yield cancel();
  }

  try {
    const userProfile: IUserProfileState = yield select(getUserProfile);

    const parsedPagesContent: [ExportPage] = JSON.parse(result.payload);

    const pages: Page[] = parsedPagesContent.map(importedPage => {
      // TODO: fix formatting duplications
      const dateId = moment(importedPage.createdAt).format(`YYYY-MM-DD`);
      const page = {
        ...importedPage,
        dateId,
        userId: userProfile.userInfo!.id,
      };

      return page;
    });

    for (const page of pages) {
      yield put(createPage(page));
    }

    yield showSuccessfulImportMessage(pages.length);
  } catch (e) {
    yield put(actions.importPagesFromFileError(e));
  }
}

function* importPagesFrom750WordsSaga(action: ReturnType<typeof actions.importPagesFrom750Words>) {
  yield call(readFile, action.payload);

  const result = yield take(fileReaderChannel);

  if (result.type === getType(actions.importPagesFromFileFileReadError)) {
    yield put(actions.importPagesFromFileError(result.payload));
    yield cancel();
  }

  try {
    const pagesStrings = result.payload.split(`------ ENTRY ------`).filter((str: string) => str.length > 0);

    const userProfile: IUserProfileState = yield select(getUserProfile);

    const importedPages = pagesStrings.reduce((pages: Page[], pageString: string) => {
      const dateRegexp = new RegExp('Date:\\s+(.*)\\n');

      const dateMatchGroup = pageString.match(dateRegexp);
      const dateMatch = dateMatchGroup && dateMatchGroup[1];

      const minutesRegexp = new RegExp('\\nMinutes:\\s+(.*)\\n');
      const minutesMatch = pageString.match(minutesRegexp);

      const textRegexp = new RegExp('(?=\\n\\n)(.*\\n?)*');
      const textMatch = pageString.match(textRegexp);

      if (dateMatch && minutesMatch && textMatch) {
        const dateId = dateMatch;
        const createdDate = moment(dateId, `YYYY-MM-DD`).hour(12);
        const minutes = minutesMatch[1];

        const startedWritingDate = createdDate.clone().subtract(minutes, 'minutes');

        const text = textMatch[0];

        const page: Page = {
          userId: userProfile.userInfo!.id,
          dateId,
          text,
          createdAt: createdDate.toISOString(),
          updatedAt: createdDate.toISOString(),
          startedWritingAt: startedWritingDate.toISOString(),
          finishedWritingAt: createdDate.toISOString(),
          isDeleted: false,
          wordsGoal: 750,
          writingIntervals: [{ start: startedWritingDate.toDate(), finish: createdDate.toDate() }],
        };

        pages.push(page);
      }
      return pages;
    }, []);

    for (const page of importedPages) {
      yield put(createPage(page));
    }

    yield showSuccessfulImportMessage(importedPages.length);
  } catch (e) {
    yield put(actions.importPagesFromFileError(e));
  }
}

interface DayOneEntry {
  readonly text: string;
  readonly creationDate: string;
}

interface DayOneJSON {
  readonly entries: DayOneEntry[];
}

function* importPagesFromDayOneSaga(action: ReturnType<typeof actions.importPagesFromDayOne>) {
  yield call(readFile, action.payload);

  const result = yield take(fileReaderChannel);

  if (result.type === getType(actions.importPagesFromFileFileReadError)) {
    yield put(actions.importPagesFromFileError(result.payload));
    yield cancel();
  }

  try {
    const userProfile: IUserProfileState = yield select(getUserProfile);
    const parsedPagesContent: DayOneJSON = JSON.parse(result.payload);
    const today = moment().toISOString();

    const importedPages = parsedPagesContent.entries
      .filter(entry => {
        return entry.text !== null;
      })
      .map(dayOneEntry => {
        const page: Page = {
          userId: userProfile.userInfo!.id,
          dateId: moment(dayOneEntry.creationDate).format('YYYY-MM-DD'),
          text: dayOneEntry.text,
          createdAt: dayOneEntry.creationDate,
          updatedAt: today,
          startedWritingAt: today,
          finishedWritingAt: today,
          isDeleted: false,
          wordsGoal: userProfile.userInfo!.settings.wordsGoal,
          writingIntervals: [],
        };

        return page;
      });

    for (const page of importedPages) {
      yield put(createPage(page));
    }
    yield showSuccessfulImportMessage(importedPages.length);
  } catch (e) {
    yield put(actions.importPagesFromFileError(e));
  }
}

function* showSuccessfulImportMessage(importedCount: number) {
  yield call(toast, `${importedCount} pages where successfully imported`, {
    autoClose: 2000,
  });
}

export default function* importExportSaga() {
  yield takeEvery([getType(actions.exportAllPagesToFile)], exportAllPagesToFileSaga);
  yield takeEvery([getType(actions.importPagesFromFile)], importPagesFromFileSaga);
  yield takeEvery([getType(actions.importPagesFrom750Words)], importPagesFrom750WordsSaga);
  yield takeEvery([getType(actions.importPagesFromDayOne)], importPagesFromDayOneSaga);
}



// WEBPACK FOOTER //
// ./src/modules/importExport/saga.ts